<i18n>
[
    "browse__availability--label",
    "browse__toolTip--subheader",
    "global__gotIt",
]
</i18n>

<template>
    <div class="c-filters-availability">
        <div
            v-if="showOnDesktop"
            class="c-filters-availability__desktop"
            :data-product-tour-lightbox="CATALOG_TOUR_LIGHTBOXES.availabilityLarge.selector"
        >
            <Toggle
                ref="desktopToggle"
                :isInitiallyOn="availabilityFilterIsActive"
                :rightLabel="label"
                rightLabelClasses="o-text--body-copy o-text--caption"
                @input="toggleAvailabilityFilterStatus"
            />
            <BaseTooltip
                trigger="hover"
                :closeText="$t('global__gotIt')"
                :tooltipVerticalMargin="18"
                class="c-filters-availability__tooltip"
            >
                <template #launcher>
                    <div class="o-flex-vertical-center">
                        <AppIcon
                            icon="global--tooltip"
                            color="primary"
                            size="12px"
                        />
                    </div>
                </template>
                <template #body>
                    <p class="u-text--center o-text--caption">
                        {{ $t('browse__toolTip--subheader') }}
                    </p>
                </template>
            </BaseTooltip>
        </div>
        <div v-else class="o-row-full">
            <div
                class="o-extra-small--12 c-filters-availability__mobile"
                :data-product-tour-lightbox="CATALOG_TOUR_LIGHTBOXES.availabilitySmall.selector"
            >
                <div v-tab-focus="toggleMobileTooltip" class="o-flex-vertical-center">
                    <span
                        :id="`c-filters-availability--${uuid}__toggle-label`"
                        class="c-filters-availability__mobile-label o-text--caption u-text--gray"
                    >
                        {{ label }}
                    </span>
                    <AppIcon icon="global--tooltip" color="primary" size="12px" />
                </div>
                <Toggle
                    ref="mobileToggle"
                    :isInitiallyOn="availabilityFilterIsActive"
                    :labelledBy="`c-filters-availability--${uuid}__toggle-label`"
                    @input="toggleAvailabilityFilterStatus"
                />
            </div>
            <div v-if="showMobileTooltip" class="o-extra-small--12 c-filters-availability__mobile-help-text">
                <p class="o-text--caption u-text--gray">
                    {{ $t('browse__toolTip--subheader') }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { uniqueId } from 'lodash-es';
import { useI18n } from 'vue-i18n';

import { useActions, useState } from '~coreModules/core/js/composables/vuexHelpers';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { CATALOG_QUERY_PAGE } from '~coreModules/catalog/js/catalog-constants';

import { CATALOG_QUERY_IN_STOCK } from '~modules/catalog/js/catalog-constants';
import { CATALOG_AVAILABILITY_UPDATED } from '~modules/core/js/global-event-constants';
import { CATALOG_TOUR_LIGHTBOXES } from '~modules/product-tour/js/product-tour-constants';
import {
    SITE_SETTINGS_MODULE_NAME,
    UPDATE_AVAILABILITY_FILTER_PREFERENCE,
} from '~modules/site-settings/js/site-settings-store';

import Toggle from '~coreModules/core/components/ui/form-elements/radios/Toggle.vue';
import BaseTooltip from '~coreModules/core/components/ui/tooltips/BaseTooltip.vue';

const props = defineProps({
    /* dynamically specifying the context to which the filterGroups are, not passing filtergroups in as a prop
    because when doing so it is not reactive */
    catalogContext: {
        type: String,
        required: true,
    },
    customLabel: {
        type: String,
        default: null,
    },
    showOnDesktop: {
        type: Boolean,
        default: true,
    },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const uuid = uniqueId();

const { applyAvailabilityFilterPreference } = useState(SITE_SETTINGS_MODULE_NAME, {
    applyAvailabilityFilterPreference: state => state.preferences.applyAvailabilityFilterPreference,
});

// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const { availabilityFilterIsActive, respectAvailabilityFilterPreference } = useState(props.catalogContext, {
    availabilityFilterIsActive: state => state.catalog.availabilityFilterIsActive,
    respectAvailabilityFilterPreference: state => state.catalog.respectAvailabilityFilterPreference,
});

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });
const { updateAvailabilityFilterPreference } = useActions(SITE_SETTINGS_MODULE_NAME, {
    updateAvailabilityFilterPreference: UPDATE_AVAILABILITY_FILTER_PREFERENCE,
});

const showMobileTooltip = ref(false);
const desktopToggle = ref(null);
const mobileToggle = ref(null);

const label = computed(() => props.customLabel || t('browse__availability--label'));

function updateToggleState(newValue) {
    const inStock = newValue !== false && newValue !== 'false';

    if (desktopToggle.value) {
        desktopToggle.value.setToggleStatus(inStock);
    }

    if (mobileToggle.value) {
        mobileToggle.value.setToggleStatus(inStock);
    }
}

function toggleAvailabilityFilterStatus() {
    const { query } = route;
    const newQuery = { ...query };
    const activeInStockQuery = query?.[CATALOG_QUERY_IN_STOCK];
    let appliedInStockFilter;

    if (respectAvailabilityFilterPreference.value) {
        appliedInStockFilter = activeInStockQuery || applyAvailabilityFilterPreference.value;

        if (appliedInStockFilter === true || appliedInStockFilter === 'true') {
            newQuery[CATALOG_QUERY_IN_STOCK] = 'false';
            updateAvailabilityFilterPreference('false');
        } else {
            newQuery[CATALOG_QUERY_IN_STOCK] = 'true';
            updateAvailabilityFilterPreference('true');
        }
    } else {
        appliedInStockFilter = activeInStockQuery || null;
        newQuery[CATALOG_QUERY_IN_STOCK] = appliedInStockFilter === true || appliedInStockFilter === 'true' ?
            'false' : 'true';
    }

    /* remove page param for all new filter requests */
    delete newQuery[CATALOG_QUERY_PAGE];

    trackGlobalEvent({
        type: CATALOG_AVAILABILITY_UPDATED,
        data: {
            label: newQuery[CATALOG_QUERY_IN_STOCK],
        },
    });

    router.replace({
        query: newQuery,
    });

}

function toggleMobileTooltip() {
    showMobileTooltip.value = !showMobileTooltip.value;
}

watch(() => route.query?.[CATALOG_QUERY_IN_STOCK], (newValue) => {
    if (newValue !== undefined) {
        updateToggleState(newValue);
    }
});

onBeforeMount(() => {
    if (!props.showOnDesktop) {
        showMobileTooltip.value = true;
    }
});
</script>

<style lang="scss">
    .c-filters-availability {
        width: 100%;

        &__desktop {
            color: $nu-gray;
            height: 40px;
            border-radius: 2px;
            background-color: $nu-white;
            padding: $nu-spacer-1 $nu-spacer-2;
            display: flex;
        }

        &__mobile {
            width: 100%;
            display: flex;
            justify-content: space-between;
        }

        &__mobile-label {
            margin-right: $nu-spacer-1;
        }

        &__mobile-help-text {
            margin-top: $nu-spacer-2;
            text-transform: none;
        }

        &__tooltip {
            margin-left: $nu-spacer-1;
            display: flex;
            align-items: center;
        }

        &__label {
            margin-left: $nu-spacer-1pt5;
        }
    }

</style>
